import { Disclosure, Transition } from "@headlessui/react";
import { ChevronDown } from "lucide-react";
import { motion } from "framer-motion";

const faqs = [
  {
    question: "What is CauseConnect & CORAA?",
    answer: "CauseConnect is a unified platform for corporate volunteering and nonprofit collaboration. CORAA is the AI co-pilot that powers everything from strategy to automation."
  },
  {
    question: "Is this just for nonprofits?",
    answer: "Nope! CauseConnect is built for both nonprofits and corporates. Whether you're managing events or aligning ESG goals, CORAA supports your mission."
  },
  {
    question: "Can I bring in my existing NPO partners?",
    answer: "Yes, you can onboard all your current partners, convert their decks to live listings, and plug them into your CSR or volunteering strategy."
  },
  {
    question: "Does CORAA help with benchmarking?",
    answer: "Yes! CORAA compares your data against 2,000+ companies across volunteering, CSR, and ESG."
  },
  {
    question: "How smart is the AI Assistant?",
    answer: "CORAA answers queries like a teammate, suggesting campaigns, NPOs, benchmarks, and more."
  },
  {
    question: "Do I need to be tech-savvy to use this?",
    answer: "Not at all. If you can upload a document or send an email, you can use CauseConnect."
  },
  {
    question: "Does CORAA support global teams?",
    answer: "Yes, it's multilingual and built to support strategy across locations, themes, and formats."
  },
  {
    question: "Can this replace our internal spreadsheets and trackers?",
    answer: "Yes. CORAA turns your documents into live dashboards, campaign planners, and reporting assistants."
  },
  {
    question: "How do I get started?",
    answer: "We’d love to show you. Schedule a quick walkthrough or upload a document and let CORAA guide you from there."
  },
  {
    question: "Can I track impact across different locations and themes?",
    answer: "Yes! CORAA helps you tag and analyze volunteering programs across geographies, SDGs, and observance days."
  },
  {
    question: "Can I compare my volunteering programs with other companies?",
    answer: "Absolutely. CORAA shows you how peers run campaigns and what benchmarks to aim for."
  },
  {
    question: "Does CauseConnect help with NPO discovery?",
    answer: "Yes, CORAA lets you discover vetted NPOs by theme, geography, risk filters, and more."
  },
  {
    question: "What if I already use a different volunteering platform?",
    answer: "No worries. You can sync your existing data into CORAA for benchmarking, insights, and reporting."
  },
  {
    question: "Can I use CORAA for ESG goals too?",
    answer: "Yes. CORAA supports ESG reporting, goal alignment, and disclosure benchmarking,all in one place."
  },
  {
    question: "Does the system generate content?",
    answer: "Yes! CORAA can draft social media posts, donor emails, impact reports, and campaign pitches instantly."
  },
  {
    question: "Can my team collaborate inside CauseConnect?",
    answer: "Yes. Role-based access and a unified dashboard make cross-functional teamwork simple."
  },
  {
    question: "Is this secure for sensitive data?",
    answer: "Yes. All data is stored securely with enterprise-grade protection and custom permissions."
  },
  {
    question: "Can I run UN day aligned campaigns from here?",
    answer: "Definitely. CORAA has prebuilt suggestions for every UN observance day to make campaign planning effortless."
  },
  {
    question: "Does CORAA support employee volunteering programs?",
    answer: "Yes. CORAA helps you design, localize, and measure volunteering programs that employees love."
  },
  {
    question: "Can CORAA help with donor or leadership reporting?",
    answer: "Yes. It generates crisp, visual impact stories from your campaigns in seconds."
  },
  {
    question: "Is this just for big companies?",
    answer: "No. We support small teams and global enterprises alike,CORAA scales with you."
  },
  {
    question: "Can I get a custom demo?",
    answer: "Of course. Reach out hello@causeonnect.ai and we’ll personalize a walkthrough for your needs."
  },
  {
    question: "Is CORAA trained on global data?",
    answer: "Yes. It’s trained on millions of real-world data points across geographies, sectors, and themes."
  },
  {
    question: "Still have questions?",
    answer: "Let’s talk. Book a demo or drop us a line at hello@causeconnect.ai we’ll walk you through what CORAA can do for you."
  }
];

export const FAQSection = () => {
  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          <p className="text-xl text-gray-600">Explore how CauseConnect and CORAA work for you</p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
            >
              <Disclosure as="div" className="mt-4">
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-white px-4 py-4 text-left text-lg font-medium text-gray-900 shadow-sm hover:bg-gray-50">
                      <span>{faq.question}</span>
                      <ChevronDown className={`h-6 w-6 text-gray-500 transition-transform duration-200 ${open ? "rotate-180 transform" : ""}`} />
                    </Disclosure.Button>
                    <Transition enter="transition duration-100 ease-out" enterFrom="opacity-0" enterTo="opacity-100">
                      <Disclosure.Panel className="px-4 pt-4 pb-2 text-gray-600">{faq.answer}</Disclosure.Panel>
                    </Transition>
                  </>
                )}
              </Disclosure>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
